import {
    CellBadge,
    DataTable,
    DataTableColumnHeader,
    DataTableControls,
    DataTablePagination,
} from 'src/registry/components/table';
import { useIntl } from 'react-intl';
import useSearch from 'src/registry/hooks/useSearch';
import { createColumnHelper } from '@tanstack/react-table';
import { commonMessages, generalMessages } from 'src/utils/messages';
import { servicePlansMessages } from 'src/features/servicePlans/utils/messages';
import { useParams } from 'react-router-dom';
import { ServicePlanTerm } from '../../utils/types';
import { servicePlanTermsMessages } from 'src/features/servicePlanTerms/utils/messages';
import { useListServicePlanTerms } from 'src/features/servicePlanTerms/api/useListServicePlanTerms';
import { CellCurrencyBadge } from 'src/features/moneyAccounts/components/tables/CellCurrencyBadge';
import CreateServicePlanTermFormModal from 'src/features/servicePlanTerms/components/forms/CreateServicePlanTermFormModal';
import CellServicePlanTermActions from 'src/features/servicePlanTerms/components/tables/CellServicePlanTermActions';
import DataTableColumnHeaderWrap from 'src/features/servicePlanTerms/components/DataTableColumnHeaderWrap';
import { BILLING_STATUS_BADGE_OPTIONS } from 'src/features/servicePlanBillingPolicies/utils/constants';
import useFilters from 'src/registry/hooks/useFilters';
import React from 'react';
import { INPUT_TYPE } from 'src/registry/components/table/utils/constants';
import CellDate from 'src/registry/components/table/cells/CellDate';
import useSorting from 'src/registry/hooks/useSorting';
import useHasPermissions from 'src/utils/useHasPermissions';
import { PERMISSION } from 'src/features/auth/types';
import { BILLING_STATUS } from 'src/features/servicePlanBillingPolicies/utils/types';

type ServicePlanTermsTableProps = {
    isServicePlanActive?: boolean;
};

const ServicePlanTermsTable = ({ isServicePlanActive }: ServicePlanTermsTableProps) => {
    const { formatMessage } = useIntl();
    const { servicePlanId = '' } = useParams();

    const permissionManageServicePlans = useHasPermissions([PERMISSION.MANAGE_SERVICE_PLANS]);

    const [dateNow] = React.useState(() => Date.now());

    const { querySearchOptions, ...search } = useSearch();
    const { queryFiltersOptions, ...filters } = useFilters();
    const { querySortingOptions, ...sorting } = useSorting({
        initialSortingState: [{ id: 'version_number', desc: false }],
    });

    const {
        data: servicePlanTerms,
        isLoading,
        isError,
    } = useListServicePlanTerms({
        ...querySearchOptions,
        ...queryFiltersOptions,
        ...querySortingOptions,
        servicePlanId: servicePlanId,
        config: { enabled: !!servicePlanId },
    });

    const columnHelper = createColumnHelper<ServicePlanTerm>();

    const columns = [
        columnHelper.accessor('version_number', {
            header: DataTableColumnHeader,
            enableSorting: true,
            meta: {
                title: formatMessage(servicePlansMessages.version),
            },
            maxSize: 45,
        }),
        columnHelper.accessor('status', {
            header: DataTableColumnHeader,
            enableHiding: true,
            cell: CellBadge,
            meta: {
                title: formatMessage(servicePlansMessages.status),
                badgeOptions: BILLING_STATUS_BADGE_OPTIONS,
            },
        }),
        columnHelper.accessor('valid_from', {
            enableSorting: true,
            header: DataTableColumnHeader,
            cell: CellDate,
            enableHiding: true,
            enableColumnFilter: true,
            meta: {
                title: formatMessage(generalMessages.validFrom),
                inputType: INPUT_TYPE.DATE,
                dateFilter: {
                    afterDate: new Date(dateNow - 30 * 24 * 60 * 60 * 1000),
                    beforeDate: new Date(),
                },
                inlineDateFilter: true,
            },
        }),
        columnHelper.accessor('valid_to', {
            enableSorting: true,
            header: DataTableColumnHeader,
            enableHiding: true,
            enableColumnFilter: true,
            cell: CellDate,
            meta: {
                title: formatMessage(generalMessages.validTo),
                inputType: INPUT_TYPE.DATE,
                dateFilter: {
                    afterDate: new Date(dateNow - 30 * 24 * 60 * 60 * 1000),
                    beforeDate: new Date(),
                },
                inlineDateFilter: true,
            },
        }),
        columnHelper.accessor('currency', {
            enableSorting: true,
            header: DataTableColumnHeader,
            enableHiding: true,
            cell: ({ row }) => {
                return <CellCurrencyBadge currency={row.original.currency} />;
            },
            meta: {
                title: formatMessage(commonMessages.currency),
            },
        }),
        columnHelper.accessor('trial_period_days', {
            enableSorting: true,
            header: () => <DataTableColumnHeaderWrap title={formatMessage(servicePlanTermsMessages.trialPeriodDays)} />,
            cell: ({ row }) => {
                return <div className="w-full text-center">{row.original.trial_period_days ?? '-'}</div>;
            },
            enableHiding: true,
            minSize: 115,
        }),
        columnHelper.accessor('unpaid_grace_period_days', {
            enableSorting: true,
            header: () => <DataTableColumnHeaderWrap title={formatMessage(servicePlansMessages.unpaidGraceDays)} />,
            cell: ({ row }) => {
                return <div className="w-full text-center">{row.original.unpaid_grace_period_days ?? '-'}</div>;
            },
            enableHiding: true,
            minSize: 140,
        }),
        columnHelper.accessor('lock_warning_days', {
            enableSorting: true,
            header: () => <DataTableColumnHeaderWrap title={formatMessage(servicePlanTermsMessages.lockWarningDays)} />,
            cell: ({ row }) => {
                return <div className="w-full text-center">{row.original.lock_warning_days ?? '-'}</div>;
            },
            enableHiding: true,
            minSize: 200,
        }),
        columnHelper.accessor('lock_grace_period_days', {
            enableSorting: true,
            header: () => <DataTableColumnHeaderWrap title={formatMessage(servicePlansMessages.lockGraceDays)} />,
            cell: ({ row }) => {
                return <div className="w-full text-center">{row.original.lock_grace_period_days ?? '-'}</div>;
            },
            enableHiding: true,
            minSize: 110,
        }),
        columnHelper.accessor('stop_warning_days', {
            enableSorting: true,
            header: () => <DataTableColumnHeaderWrap title={formatMessage(servicePlansMessages.stopWarningDays)} />,
            cell: ({ row }) => {
                return <div className="w-full text-center">{row.original.stop_warning_days ?? '-'}</div>;
            },
            enableHiding: true,
            minSize: 200,
        }),
        columnHelper.accessor('stop_grace_period_days', {
            enableSorting: true,
            header: () => <DataTableColumnHeaderWrap title={formatMessage(servicePlansMessages.stopGraceDays)} />,
            cell: ({ row }) => {
                return <div className="w-full text-center">{row.original.stop_grace_period_days ?? '-'}</div>;
            },
            enableHiding: true,
            minSize: 120,
        }),
        ...(permissionManageServicePlans
            ? [
                  columnHelper.display({
                      id: 'actions',
                      header: DataTableColumnHeader,
                      cell: (ctx) => {
                          const allowed = !isServicePlanActive || ctx.row.original.status === BILLING_STATUS.SCHEDULED;
                          return allowed ? <CellServicePlanTermActions {...ctx} /> : null;
                      },
                      maxSize: 45,
                  }),
              ]
            : []),
    ];
    return (
        <DataTable<ServicePlanTerm, unknown>
            columns={columns}
            data={servicePlanTerms}
            semantics={{
                title: formatMessage(servicePlanTermsMessages.servicePlanTerms),
                entryName: formatMessage(servicePlanTermsMessages.term).toLowerCase(),
                entryNames: formatMessage(servicePlanTermsMessages.terms).toLowerCase(),
                placeholder: formatMessage(servicePlanTermsMessages.term).toLowerCase(),
            }}
            search={search}
            filters={filters}
            sorting={sorting}
            isError={isError}
            isLoading={isLoading}
            getRowId={(row) => row.id.toString()}
            components={{
                controls: DataTableControls,
                pagination: DataTablePagination,
                ...(permissionManageServicePlans && { newEntry: CreateServicePlanTermFormModal }),
            }}
        />
    );
};

export default ServicePlanTermsTable;
